import axios from 'axios';
import { useEffect, useState } from 'react';

import type { StripeSubscription } from '@flintmint/core-shared/firestore/schemas';
import { isLiveSubscriptionStatus } from '@flintmint/core-shared/utils';
import { useToaster } from '@flintmint/web-shared/hooks/use-toaster';

import { useUserAccount } from '@/hooks/use-user-account';
import { useServices } from '@/providers/services-provider';
import type { ApiService } from '@/services/api-service';

/** Downgrade to Free: schedules the subscription's end. A never-paid past_due subscription ends at once. */
export const useCancelSubscription = () => {
  const { run, isPending } = useSubscriptionChange(
    (api) => api.cancelSubscription(),
    (subscription) => !subscription || !isLiveSubscriptionStatus(subscription.status) || subscription.cancelAt !== undefined
  );
  return { cancelSubscription: run, isPending };
};

/** Undoes Downgrade to Free: clears the subscription's scheduled end. */
export const useUndoCancelSubscription = () => {
  const { run, isPending } = useSubscriptionChange(
    (api) => api.undoCancelSubscription(),
    (subscription) => subscription?.cancelAt === undefined
  );
  return { undoCancelSubscription: run, isPending };
};

/**
 * Starts the paid plan now. 402 (the charge is held) and 409 (held elsewhere, or no longer a Trial) are answers too:
 * the synced subscription shows which one happened.
 */
export const useEndTrial = () => {
  const { run, isPending } = useSubscriptionChange(
    async (api) => {
      try {
        await api.endTrial();
      } catch (error) {
        if (!(axios.isAxiosError(error) && (error.response?.status === 402 || error.response?.status === 409))) throw error;
      }
    },
    (subscription) => subscription?.status !== 'trialing' || subscription.pendingInvoiceUrl !== undefined
  );
  return { endTrial: run, isPending };
};

/**
 * The request answers with no body, so the change stays pending after it until the synced subscription shows it
 * (`isSynced`), keeping the previous state from showing in between. A failed request toasts and is no longer pending.
 */
const useSubscriptionChange = (
  request: (api: ApiService) => Promise<void>,
  isSynced: (subscription: StripeSubscription | undefined) => boolean
) => {
  const { api } = useServices();
  const { showToastNotification } = useToaster();
  const { account } = useUserAccount();
  const [phase, setPhase] = useState<'idle' | 'requesting' | 'syncing'>('idle');
  const synced = isSynced(account?.stripeSubscription);

  useEffect(() => {
    if (phase === 'syncing' && synced) setPhase('idle');
  }, [phase, synced]);

  const run = async () => {
    if (!api) return;
    setPhase('requesting');
    try {
      await request(api);
      setPhase('syncing');
    } catch {
      setPhase('idle');
      showToastNotification({ type: 'error', title: 'Something went wrong. Please try again.' });
    }
  };

  return { run, isPending: phase === 'requesting' || (phase === 'syncing' && !synced) };
};
